/**
 * Skeleton — per UX_GUIDE_UNIFIED.md
 *
 * Loading placeholder that mirrors exact component dimensions.
 * Layout must not shift when data arrives.
 * Shimmer animation from bg-elevated to border and back, 1.5s cycle.
 */

import { makeStyles, mergeClasses } from '@fluentui/react-components';

const useStyles = makeStyles({
  skeleton: {
    backgroundColor: 'var(--bg-elevated)',
    borderRadius: '4px',
    position: 'relative',
    overflow: 'hidden',
    '::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      backgroundImage:
        'linear-gradient(90deg, transparent, var(--border), transparent)',
      animationName: {
        '0%': { transform: 'translateX(-100%)' },
        '100%': { transform: 'translateX(100%)' },
      },
      animationDuration: '1.5s',
      animationIterationCount: 'infinite',
      animationTimingFunction: 'ease-in-out',
    },
  },
  text: {
    height: '14px',
    marginBottom: '8px',
    borderRadius: '4px',
  },
  heading: {
    height: '20px',
    marginBottom: '12px',
    width: '60%',
    borderRadius: '4px',
  },
  row: {
    height: '44px',
    marginBottom: '4px',
    borderRadius: '4px',
  },
  card: {
    height: '120px',
    borderRadius: '8px',
    border: '1px solid var(--border)',
  },
});

export interface SkeletonProps {
  variant?: 'text' | 'heading' | 'row' | 'card' | 'custom';
  width?: string;
  height?: string;
  count?: number;
  className?: string;
}

export function Skeleton({
  variant = 'text',
  width,
  height,
  count = 1,
  className,
}: SkeletonProps) {
  const styles = useStyles();

  const variantClass =
    variant === 'text'
      ? styles.text
      : variant === 'heading'
        ? styles.heading
        : variant === 'row'
          ? styles.row
          : variant === 'card'
            ? styles.card
            : undefined;

  return (
    <>
      {Array.from({ length: count }, (_, i) => (
        <div
          key={i}
          className={mergeClasses(styles.skeleton, variantClass, className)}
          style={{
            ...(width && { width }),
            ...(height && { height }),
          }}
        />
      ))}
    </>
  );
}
